import React, { useContext, useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import { formatGermanDate, formatGermanDateShort } from "../utils/dateUtils";
import { mealInds } from "../utils/mealIndUtils";
import {
  computePlanCellCoordinates,
  CookingOverrides,
  findById,
  getMealKeyForMeal,
  getMIKey,
  GroupOverrideState,
  IngredientGroup,
  isNullIndex,
  MealKey,
  MealKeyString,
  parseMealKey,
  resolveFoodUnitId,
  resolveGroupOverrideState,
} from "../utils/planUtils";
import { MealWithIntakes } from "../data/plan";
import { DrawerHistoryContext, DrawerHistoryContextType } from "../contexts";
import { queryKeys, usePersonsAsync } from "../queries/queries";
import {
  ensureFoods,
  ensurePurchases,
  getFoodByUnit,
  invalidatePurchases,
  seedPurchase,
  useFoodsByIdsAsync,
  useMealPurchasesAsync,
  usePurchasesByIdsAsync,
} from "../queries/caches";
import { collectFoodIds, collectFoodUnitIds } from "../utils/foodIdUtils";
import { useQueryClient } from "@tanstack/react-query";
import { Plan2PlateDrawer, usePlan2PlateDisclosure } from "./Plan2PlateDrawer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/Accordion";
import { FoodUnit } from "../data/foodUnit";
import { IngredientRestoreRow } from "./dish/IngredientRow";
import { DishModification } from "../data/dishModification";
import { DishVersionWarning } from "./dish/DishVersionWarning";
import { FoodDto, PurchaseDto } from "../api";
import { loadDishModification } from "../loaders";
import { AmountUnit } from "./UnitAmount";
import { formatFoodDescription } from "../utils/foodUtils";
import { getFoodMaybe } from "../utils/foodUtils";
import {
  CookingCreatePurchaseDrawer,
  CookingProductPicker,
} from "./cooking/CookingProductPicker";
import { findMatchingUnit } from "../utils/foodUtils";
import { SectionLabel } from "./ui/SectionLabel";
import { PersonNutriCard } from "./ui/PersonNutriCard";
import { StepProgressBar } from "./ui/StepProgressBar";
import {
  buildDishGroups,
  computePersonNutri,
} from "./cooking/cookingDataUtils";
import { StepIngredientCard } from "./cooking/StepIngredientCard";
import { DishTagSelector } from "./cooking/DishTagSelector";
import { QueryBoundary } from "./ErrorBoundary";
import { LookupSkeleton } from "./ui/LookupSkeleton";
import { useHorizontalSwipe } from "../utils/useHorizontalSwipe";
import { Glyph } from "./SmallIconButton";

// ─── Props & hook ─────────────────────────────────────────────────────────────

interface CookingDrawerProps {
  meals: MealWithIntakes[] | undefined;
  isOpen: boolean;
  onClose: () => void;
  onConsume: (
    date: string,
    mealInd: string,
    mealKey: MealKey,
    overrides: CookingOverrides,
    personId?: number,
  ) => void;
  onDelete: (
    date: string,
    mealInd: string,
    mealKey: MealKey,
    personId?: number,
  ) => void;
  clearHistory: () => Promise<void>;
}

export function useCookingDrawer(
  onConsume: CookingDrawerProps["onConsume"],
  onDelete: CookingDrawerProps["onDelete"],
  drawerHistoryContext: DrawerHistoryContextType,
): CookingDrawerProps & {
  cook: (planCells: MealWithIntakes[]) => Promise<void>;
} {
  const [planCells, updatePlanCells] = useImmer<MealWithIntakes[] | undefined>(
    undefined,
  );
  const { isOpen, onOpen, onClose } =
    usePlan2PlateDisclosure(drawerHistoryContext);

  async function cook(planCells: MealWithIntakes[]) {
    updatePlanCells(planCells);
    onOpen();
  }

  return {
    onConsume,
    onDelete,
    cook,
    meals: planCells,
    isOpen,
    onClose,
    clearHistory: drawerHistoryContext.clearHistory,
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

export const CookingDrawer = (props: CookingDrawerProps) => {
  if (!props.isOpen) return null;
  return (
    <QueryBoundary fallback={null}>
      <CookingDrawerContent {...props} />
    </QueryBoundary>
  );
};

function CookingDrawerContent(props: CookingDrawerProps) {
  const personsList = usePersonsAsync(false);
  const queryClient = useQueryClient();
  const loadPurchase = async () => {
    await invalidatePurchases(queryClient);
  };
  const drawerHistoryContext = useContext(DrawerHistoryContext);

  // ── Nested drawer disclosures ─────────────────────────────────────────────
  const stepDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "step-cooking",
  );
  const productDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "product-selection",
  );
  const createPurchaseDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "create-purchase",
  );

  // ── State ─────────────────────────────────────────────────────────────────
  const [activeDishIndex, setActiveDishIndex] = useState(0);
  const [cookingStepIndex, setCookingStepIndex] = useState(0);
  // Nur den Key merken und die Gruppe bei jedem Render frisch aus den
  // aktuellen `dishGroups` ziehen. Ein gemerktes Gruppen-Objekt wäre veraltet,
  // sobald Foods/Purchases nachgeladen werden (falsches `isFoodBased`, leerer
  // Name, fehlender "Planwert").
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [overrides, updateOverrides] = useImmer<CookingOverrides>({});
  const [loadedDishes, setLoadedDishes] = useState<
    Record<number, DishModification>
  >({});
  // Rezepte, deren Laden fehlgeschlagen ist – dann auf die Fallback-Schritte
  // (statt eines ewigen Skeletons) zurückfallen.
  const [failedDishIds, setFailedDishIds] = useState<Record<number, boolean>>(
    {},
  );

  const meals = useMemo(() => props.meals ?? [], [props.meals]);
  const mealPurchases = useMealPurchasesAsync(meals);
  const foodIds = useMemo(
    () => collectFoodIds(meals, mealPurchases),
    [meals, mealPurchases],
  );
  // FoodUnits aus den geladenen Rezepten: direkt aus den Steps sowie aus den
  // Alternativen und PersonChoices der Steps. Nur so finden wir die Foods, die
  // als Produkt-Alternativen im Rezept hängen (und nicht in den geplanten
  // MealIngredients auftauchen).
  const dishFoodUnitIds = useMemo(
    () => [
      ...new Set(
        Object.values(loadedDishes).flatMap((dish) =>
          collectFoodUnitIds(dish.steps),
        ),
      ),
    ],
    [loadedDishes],
  );
  const mealFoodUnitIds = useMemo(() => {
    const ids = new Set<number>();
    meals.forEach((meal) =>
      meal.meal.mealIngredients.forEach((mi) => {
        if (mi.foodUnitId) ids.add(mi.foodUnitId);
      }),
    );
    return [...ids];
  }, [meals]);
  const foodUnitIds = useMemo(
    () => [...new Set([...mealFoodUnitIds, ...dishFoodUnitIds])],
    [mealFoodUnitIds, dishFoodUnitIds],
  );
  const foods = useFoodsByIdsAsync(foodIds, foodUnitIds);

  // Foods, die über die FoodUnits der Rezept-Steps/-Alternativen hängen.
  const dishFoodIds = useMemo(() => {
    const unitIds = new Set(dishFoodUnitIds);
    return foods
      .filter((food) => food.foodUnits?.some((fu) => unitIds.has(fu.id)))
      .map((food) => food.id);
  }, [dishFoodUnitIds, foods]);

  // Upfront: für alle Foods (aus Meals und aus Rezept-Steps + Alternativen)
  // einmal alle Purchases laden, damit die Produktwahl danach ohne Nachladen
  // (und ohne Flackern) aus dem Cache bedient wird.
  const allFoodIds = useMemo(
    () => [
      ...new Set([...foodIds, ...foods.map((food) => food.id), ...dishFoodIds]),
    ],
    [foodIds, foods, dishFoodIds],
  );
  const preloadedPurchases = usePurchasesByIdsAsync([], allFoodIds);
  const purchases = useMemo(() => {
    const map = new Map<number, PurchaseDto>();
    [...mealPurchases, ...preloadedPurchases].forEach((purchase) =>
      map.set(purchase.id, purchase),
    );
    return [...map.values()];
  }, [mealPurchases, preloadedPurchases]);

  useEffect(() => {
    if (!props.isOpen) {
      setLoadedDishes({});
      setFailedDishIds({});
    }
  }, [props.isOpen, setLoadedDishes]);

  // ── Load dish steps when drawer is open ────────────────────────────────────
  useEffect(() => {
    if (!props.isOpen || !props.meals?.length) return;
    const dishIds = [
      ...new Set(
        props.meals
          .map((m) => m.meal.dishId)
          .filter((id): id is number => !!id && id !== -1),
      ),
    ];

    async function loadAll() {
      const nextDishes = { ...loadedDishes };
      let changed = false;
      for (const id of dishIds) {
        if (!nextDishes[id]) {
          try {
            const dish = await loadDishModification(id);
            // Rezept sofort übernehmen: Die Step-Struktur darf nicht davon
            // abhängen, ob die Foods/Purchases vollständig vorgeladen werden
            // konnten. Sonst fällt die Ansicht auf die unvollständigen
            // Zutaten-Steps zurück und zeigt zu wenige Schritte.
            nextDishes[id] = dish;
            changed = true;
            try {
              // Foods/Purchases des Rezepts (inkl. Alternativen/PersonChoices)
              // verbindlich laden, bevor der Schritt gerendert wird – sonst
              // fehlen Alternativen-Chips bzw. Produkt-Optionen (Race).
              const unitIds = collectFoodUnitIds(dish.steps);
              await ensureFoods(queryClient, [], unitIds);
              const foodIds = [
                ...new Set(
                  unitIds
                    .map((unitId) => getFoodByUnit(queryClient, unitId)?.id)
                    .filter((foodId): foodId is number => foodId != null),
                ),
              ];
              await ensurePurchases(queryClient, [], foodIds);
            } catch (e) {
              console.error("Rezept-Lookups unvollständig", e);
            }
          } catch (e) {
            console.error("Error loading dish steps:", e);
            setFailedDishIds((prev) => ({ ...prev, [id]: true }));
          }
        }
      }
      if (changed) {
        setLoadedDishes(nextDishes);
      }
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.meals, props.isOpen]);

  // ── Pre-populate overrides from props.meals ────────────────────────────────
  useEffect(() => {
    if (!props.meals?.length) return;
    const initialOverrides: CookingOverrides = {};
    const activeDishGroups = buildDishGroups(
      props.meals,
      foods,
      purchases,
      loadedDishes,
    );
    for (const dish of activeDishGroups) {
      for (const group of dish.allIngredientGroups) {
        const personAmounts: Record<number, number> = {};
        let allZero = true;
        group.pairs.forEach((p) => {
          personAmounts[p.meal.meal.personId] = p.mi.amount;
          if (p.mi.amount > 0) allZero = false;
        });

        const allSynthetic = group.pairs.every((p) => p.mi.id === -1);
        const deleted = allSynthetic || allZero;

        const firstMi = group.pairs[0].mi;
        const isFoodBased = !isNullIndex(firstMi.foodUnitFoodId);
        const unitId = isFoodBased
          ? firstMi.foodUnitId
          : isNullIndex(firstMi.purchaseUnitId)
            ? undefined
            : firstMi.purchaseUnitId;
        const purchaseId = isFoodBased
          ? undefined
          : isNullIndex(firstMi.purchaseUnitPurchaseId)
            ? undefined
            : firstMi.purchaseUnitPurchaseId;
        initialOverrides[group.key] = {
          personAmounts,
          unitId,
          purchaseId,
          deleted,
        };
      }
    }
    // Nur fehlende Keys ergänzen – bereits vom Nutzer gesetzte Werte (Produkt-/
    // Alternativen-Wahl) dürfen beim Nachladen von Foods/Purchases/Dishes nicht
    // überschrieben werden.
    updateOverrides((draft) => {
      for (const [key, override] of Object.entries(initialOverrides)) {
        if (!(key in draft)) {
          draft[key] = override;
        }
      }
    });
  }, [
    props.meals,
    foods,
    purchases,
    loadedDishes,
    props.isOpen,
    updateOverrides,
  ]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const dishGroups = useMemo(() => {
    if (!meals.length) return [];
    return buildDishGroups(meals, foods, purchases, loadedDishes);
  }, [meals, foods, purchases, loadedDishes]);

  // Frische Gruppe zum gemerkten Key (Step-Gruppen haben Vorrang, da sie den
  // step-spezifischen `recipeIngredient` tragen).
  const groupByKey = useMemo(() => {
    const map = new Map<string, IngredientGroup>();
    for (const dish of dishGroups) {
      for (const group of dish.allIngredientGroups) map.set(group.key, group);
      for (const step of dish.steps) {
        for (const group of step.ingredientGroups) map.set(group.key, group);
      }
    }
    return map;
  }, [dishGroups]);

  const selectedGroup = selectedGroupKey
    ? groupByKey.get(selectedGroupKey) ?? null
    : null;

  // Vor den Early-Returns berechnet, damit die Hooks (Swipe) unbedingt laufen.
  const activeDish = dishGroups[activeDishIndex] ?? dishGroups[0];
  const steps = activeDish?.steps ?? [];
  // Solange das Rezept (DishModification) des aktiven Gerichts nicht geladen ist,
  // fehlen `recipeIngredient`/Alternativen. Statt der unvollständigen Schritt-
  // Ansicht wird ein Ladeplatzhalter gezeigt (non-suspending Race vermeiden).
  const activeDishId =
    !activeDish || activeDish.isNoDishGroup
      ? undefined
      : activeDish.meals[0]?.meal.dishId;
  const recipeLoading =
    !isNullIndex(activeDishId) &&
    !loadedDishes[activeDishId!] &&
    !failedDishIds[activeDishId!];

  // ── Horizontal-Wischen: links = nächster Schritt, rechts = vorheriger ─────
  const stepSwipe = useHorizontalSwipe({
    enabled: !recipeLoading && steps.length > 1,
    onSwipeLeft: () =>
      setCookingStepIndex((i) => Math.min(steps.length - 1, i + 1)),
    onSwipeRight: () => setCookingStepIndex((i) => Math.max(0, i - 1)),
  });

  if (!meals?.length) return <></>;
  if (!activeDish) return <></>;

  const { date, mealInd } = computePlanCellCoordinates(meals[0]);
  const mealIndName = mealInds[mealInd]?.name ?? mealInd;
  const dateLabel = formatGermanDate(date);

  const currentDishVersion =
    activeDishId != null ? loadedDishes[activeDishId]?.version : undefined;
  const dishIsOutdated =
    currentDishVersion != null &&
    activeDish.meals.some(
      (mealWithIntake) =>
        mealWithIntake.meal.dishVersion != null &&
        currentDishVersion > mealWithIntake.meal.dishVersion,
    );

  const personIds = [...new Set(meals.map((m) => m.meal.personId))];
  const persons = personIds
    .map((pid) => findById(personsList, pid))
    .filter(Boolean) as { id: number; name: string }[];

  // Step drawer data
  const hasStepNav =
    steps.length > 1 ||
    (steps.length === 1 && steps[0].stepIndex !== undefined);
  const currentStep = steps[cookingStepIndex] ?? steps[0];
  const isFirstStep = cookingStepIndex === 0;
  const isLastStep = cookingStepIndex >= steps.length - 1;

  // Progress
  const allGroups = activeDish.allIngredientGroups;
  const activeGroups = allGroups.filter((g) => !overrides[g.key]?.deleted);
  const totalCapturable = activeGroups.filter((g) => g.isFoodBased).length;

  const capturedCount = activeGroups.filter(
    (g) => g.isFoodBased && overrides[g.key]?.purchaseId !== undefined,
  ).length;

  // ── Helper to resolve actual current values (override or default) ──────────
  function getGroupOverride(group: IngredientGroup): GroupOverrideState {
    return resolveGroupOverrideState(
      group,
      overrides[group.key],
      purchases,
      foods,
    );
  }

  // Zutaten des aktuellen Schritts, die tatsächlich sichtbar sind (nicht
  // gelöscht und mit Menge > 0). Ist die Liste leer, darf der umrandete
  // Zutaten-Container gar nicht erst gerendert werden – sonst bliebe nur ein
  // leerer Rahmen stehen, der wie ein Trenner wirkt.
  const visibleStepGroups = (currentStep?.ingredientGroups ?? []).filter(
    (group) => {
      const groupState = getGroupOverride(group);
      return !groupState.deleted && groupState.totalAmount > 0;
    },
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  function openStepDrawer() {
    setCookingStepIndex(0);
    stepDisclosure.onOpen();
  }

  function openProductSelection(group: IngredientGroup) {
    setSelectedGroupKey(group.key);
    productDisclosure.onOpen();
  }

  function handleCaptureProduct(purchaseId: number | null) {
    if (!selectedGroup) return;
    const groupState = getGroupOverride(selectedGroup);
    const oldNetWeight = groupState.units.current?.netWeight || 1;
    const oldUnitId = groupState.units.unitId;
    const oldPurchase = groupState.purchaseId;
    const oldPurchaseUnit = oldPurchase
      ? findById(purchases, oldPurchase)?.purchaseUnits.find(
          (pu) => pu.id == oldUnitId,
        )
      : undefined;

    updateOverrides((draft) => {
      let override = draft[selectedGroup.key];
      if (!override) {
        override = {
          personAmounts: {},
          unitId: oldUnitId,
          purchaseId: purchaseId,
        };
        selectedGroup.pairs.forEach((p) => {
          override!.personAmounts[p.meal.meal.personId] = p.mi.amount;
        });
        draft[selectedGroup.key] = override;
      }

      override.purchaseId = purchaseId;

      const applyNetWeightRatio = (newNetWeight: number) => {
        const ratio = oldNetWeight / newNetWeight;
        for (const pId of Object.keys(override.personAmounts)) {
          const personId = Number(pId);
          override.personAmounts[personId] =
            override.personAmounts[personId] * ratio;
        }
      };

      if (purchaseId === null || purchaseId === undefined) {
        const firstIngredient = selectedGroup.pairs[0].mi;
        // Aktuell gewählte FoodUnit (Alternative) beibehalten: bei food kind die
        // FoodUnit selbst, bei purchase kind die `foodUnitId` der PurchaseUnit.
        const activeFoodUnitId =
          groupState.units.kind === "food"
            ? groupState.units.current?.id
            : groupState.units.current?.foodUnitId;
        if (selectedGroup.isFoodBased) {
          override.unitId =
            activeFoodUnitId ??
            (isNullIndex(firstIngredient.foodUnitId)
              ? undefined
              : firstIngredient.foodUnitId);
        } else {
          // For purchase-based: "Planwert" resets to originally planned purchase
          override.purchaseId = isNullIndex(
            firstIngredient.purchaseUnitPurchaseId,
          )
            ? undefined
            : firstIngredient.purchaseUnitPurchaseId;
          override.unitId = isNullIndex(firstIngredient.purchaseUnitId)
            ? undefined
            : firstIngredient.purchaseUnitId;
        }
        const newNetWeight =
          activeFoodUnitId != null
            ? getFoodMaybe(activeFoodUnitId, foods)?.foodUnits.find(
                (fu) => fu.id === activeFoodUnitId,
              )?.netWeight ?? 1
            : 1;
        applyNetWeightRatio(newNetWeight);
      } else {
        const purchase = findById(purchases, purchaseId);
        if (purchase && purchase.purchaseUnits?.length > 0) {
          // For food-based: match by foodUnitId; for purchase-based: match by the
          // foodUnitId of the currently active purchase unit
          let targetFoodUnitId: number | undefined;
          if (selectedGroup.isFoodBased) {
            targetFoodUnitId = oldUnitId;
          } else if (oldUnitId) {
            const currentPurchaseId = groupState.purchaseId;
            if (currentPurchaseId && currentPurchaseId !== purchaseId) {
              const currentPurchase = findById(purchases, currentPurchaseId);
              targetFoodUnitId = currentPurchase?.purchaseUnits?.find(
                (pu) => pu.id === oldUnitId,
              )?.foodUnitId;
            }
          }

          let newUnitId: number | undefined;
          let newNetWeight = 1;

          const matchingPU =
            purchase.purchaseUnits.find(
              (pu) =>
                pu.foodUnitId === targetFoodUnitId &&
                pu.foodUnitId != undefined,
            ) ??
            purchase.purchaseUnits.find(
              (pu) =>
                pu.netWeight == oldPurchaseUnit?.netWeight ||
                pu.description == oldPurchaseUnit?.description,
            );
          if (matchingPU) {
            newUnitId = matchingPU.id;
            newNetWeight = matchingPU.netWeight || 1;
          } else {
            // No matching purchase unit, look for fallback: first with netWeight != 1, otherwise the first unit
            let fallbackPU = purchase.purchaseUnits.find(
              (pu) => pu.netWeight === 1,
            );
            if (!fallbackPU) {
              fallbackPU = purchase.purchaseUnits[0];
            }

            if (fallbackPU) {
              newUnitId = fallbackPU.id;
              newNetWeight = fallbackPU.netWeight || 1;
            }
          }

          if (newUnitId) {
            override.unitId = newUnitId;
            applyNetWeightRatio(newNetWeight);
          }
        }
      }
    });
    productDisclosure.onClose();
  }

  function handleTotalAmountChange(
    groupKey: string,
    group: IngredientGroup,
    newTotal: number,
  ) {
    updateOverrides((draft) => {
      let override = draft[groupKey];
      if (!override) {
        const firstIngredient = group.pairs[0].mi;
        const defaultUnitId = isNullIndex(firstIngredient.foodUnitId)
          ? undefined
          : firstIngredient.foodUnitId;
        override = {
          personAmounts: {},
          unitId: defaultUnitId,
          purchaseId: undefined,
        };
        group.pairs.forEach((p) => {
          override!.personAmounts[p.meal.meal.personId] = p.mi.amount;
        });
        draft[groupKey] = override;
      }

      const oldTotal = Object.values(override.personAmounts).reduce(
        (sum, a) => sum + a,
        0,
      );
      const numPersons = group.pairs.length;

      if (oldTotal > 0) {
        const ratio = newTotal / oldTotal;
        for (const pId of Object.keys(override.personAmounts)) {
          const personId = Number(pId);
          override.personAmounts[personId] =
            override.personAmounts[personId] * ratio;
        }
      } else {
        const equalShare = newTotal / numPersons;
        for (const pId of Object.keys(override.personAmounts)) {
          const personId = Number(pId);
          override.personAmounts[personId] = equalShare;
        }
      }

      override.deleted = newTotal === 0;
    });
  }

  function handleTotalUnitChange(
    groupKey: string,
    group: IngredientGroup,
    newUnit: AmountUnit | undefined,
  ) {
    const groupState = getGroupOverride(group);
    const oldNetWeight = groupState.units.current?.netWeight || 1;
    const newNetWeight = newUnit?.netWeight || 1;
    const ratio = oldNetWeight / newNetWeight;

    updateOverrides((draft) => {
      let override = draft[groupKey];
      if (!override) {
        override = {
          personAmounts: {},
          unitId: newUnit?.id,
          purchaseId: undefined,
        };
        group.pairs.forEach((p) => {
          override!.personAmounts[p.meal.meal.personId] = p.mi.amount * ratio;
        });
        draft[groupKey] = override;
      } else {
        for (const pId of Object.keys(override.personAmounts)) {
          const personId = Number(pId);
          override.personAmounts[personId] =
            override.personAmounts[personId] * ratio;
        }
        override.unitId = newUnit?.id;
      }
    });
  }

  function handlePersonAmountChange(
    groupKey: string,
    group: IngredientGroup,
    personId: number,
    newAmount: number,
  ) {
    updateOverrides((draft) => {
      let override = draft[groupKey];
      if (!override) {
        const firstIngredient = group.pairs[0].mi;
        const defaultUnitId = isNullIndex(firstIngredient.foodUnitId)
          ? undefined
          : firstIngredient.foodUnitId;
        override = {
          personAmounts: {},
          unitId: defaultUnitId,
          purchaseId: undefined,
        };
        group.pairs.forEach((p) => {
          override!.personAmounts[p.meal.meal.personId] = p.mi.amount;
        });
        draft[groupKey] = override;
      }
      override.personAmounts[personId] = newAmount;

      const currentTotal = Object.values(override.personAmounts).reduce(
        (sum, a) => sum + a,
        0,
      );
      override.deleted = currentTotal === 0;
    });
  }

  // ── Split total amount equally among persons ────────────────────────────────
  function handleSplitValues(groupKey: string, group: IngredientGroup) {
    updateOverrides((draft) => {
      let override = draft[groupKey];
      if (!override) {
        const firstIngredient = group.pairs[0].mi;
        const defaultUnitId = isNullIndex(firstIngredient.foodUnitId)
          ? undefined
          : firstIngredient.foodUnitId;
        override = {
          personAmounts: {},
          unitId: defaultUnitId,
          purchaseId: undefined,
        };
        group.pairs.forEach((p) => {
          override!.personAmounts[p.meal.meal.personId] = p.mi.amount;
        });
        draft[groupKey] = override;
      }
      const currentTotal = Object.values(override.personAmounts).reduce(
        (sum, a) => sum + a,
        0,
      );
      const numPersons = group.pairs.length;
      const equalShare = numPersons > 0 ? currentTotal / numPersons : 0;

      for (const pId of Object.keys(override.personAmounts)) {
        const personId = Number(pId);
        override.personAmounts[personId] = equalShare;
      }
    });
  }

  function handleDeleteGroup(groupKey: string, group: IngredientGroup) {
    updateOverrides((draft) => {
      let override = draft[groupKey];
      if (!override) {
        const firstIngredient = group.pairs[0].mi;
        const defaultUnitId = isNullIndex(firstIngredient.foodUnitId)
          ? undefined
          : firstIngredient.foodUnitId;
        override = {
          personAmounts: {},
          unitId: defaultUnitId,
          purchaseId: undefined,
        };
        group.pairs.forEach((p) => {
          override!.personAmounts[p.meal.meal.personId] = p.mi.amount;
        });
        draft[groupKey] = override;
      }
      override.deleted = true;
      if (!group.recipeIngredient) {
        for (const pId of Object.keys(override.personAmounts)) {
          const personId = Number(pId);
          override.personAmounts[personId] = 0;
        }
      }
    });
  }

  function handleSelectAlternative(
    groupKey: string,
    group: IngredientGroup,
    targetFood: FoodDto,
  ) {
    const groupState = getGroupOverride(group);
    const currentUnit = groupState.units.current;

    if (!targetFood.foodUnits || targetFood.foodUnits.length === 0) return;

    let targetUnit = targetFood.foodUnits[0];
    if (currentUnit) {
      targetUnit = findMatchingUnit(currentUnit, targetFood.foodUnits);
    }

    const currentNetWeight = currentUnit?.netWeight || 1;
    const targetNetWeight = targetUnit.netWeight || 1;
    const ratio = currentNetWeight / targetNetWeight;

    updateOverrides((draft) => {
      let override = draft[groupKey];
      if (!override) {
        override = {
          personAmounts: {},
          unitId: targetUnit.id,
          purchaseId: undefined,
        };
        group.pairs.forEach((p) => {
          override!.personAmounts[p.meal.meal.personId] = p.mi.amount * ratio;
        });
        draft[groupKey] = override;
      } else {
        override.unitId = targetUnit.id;
        override.purchaseId = undefined; // Reset brand purchase

        for (const pId of Object.keys(override.personAmounts)) {
          const personId = Number(pId);
          override.personAmounts[personId] =
            override.personAmounts[personId] * ratio;
        }
      }
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Main overview drawer
  // ══════════════════════════════════════════════════════════════════════════
  // Food des aktuell aktiven Elements der Gruppe (Alternative!) – nicht das
  // Food des ursprünglichen MealIngredients.
  const selectedGroupOverride = selectedGroup
    ? getGroupOverride(selectedGroup)
    : undefined;
  const selectedFoodId = (() => {
    if (!selectedGroup || !selectedGroupOverride) return undefined;
    if (
      selectedGroupOverride.purchaseId !== null &&
      selectedGroupOverride.purchaseId !== undefined
    ) {
      return findById(purchases, selectedGroupOverride.purchaseId)?.foodId;
    }
    if (selectedGroupOverride.units.kind === "food") {
      return (
        selectedGroupOverride.units.current?.foodId ??
        selectedGroup.pairs[0].mi.foodUnitFoodId
      );
    }
    return undefined;
  })();

  return (
    <>
      <CookingCreatePurchaseDrawer
        open={createPurchaseDisclosure.isOpen}
        onClose={createPurchaseDisclosure.onClose}
        foodUnitFoodId={selectedFoodId}
        onSubmit={(newPurchase) => {
          if (selectedGroup) {
            seedPurchase(queryClient, newPurchase);
            loadPurchase().then(() =>
              setTimeout(() => {
                handleCaptureProduct(newPurchase.id);
              }, 100),
            );
          }
        }}
      />
      <Plan2PlateDrawer
        onClose={props.onClose}
        open={props.isOpen}
        header={
          <div className="flex-1 min-w-0">
            <div className="font-bold leading-tight">{mealIndName} kochen</div>
            <div className="text-xs text-ink-muted mt-0.5">{dateLabel}</div>
          </div>
        }
        body={
          <div className="flex flex-col w-full gap-4 pb-6">
            <DishVersionWarning outdated={dishIsOutdated} />
            {/* Dish tags */}
            <DishTagSelector
              dishGroups={dishGroups}
              activeIndex={activeDishIndex}
              onSelect={(idx) => {
                setActiveDishIndex(idx);
                setCookingStepIndex(0);
              }}
            />

            {activeDish.isNoDishGroup ? (
              /* ── Direkt verzehren: Zutaten flach mit Trenner ───────────── */
              <div className="overflow-hidden rounded-card border border-line bg-surface-card shadow-card">
                {activeDish.allIngredientGroups.map((group, index) => {
                  const groupState = getGroupOverride(group);
                  const captured = groupState.purchaseId;
                  return (
                    <StepIngredientCard
                      key={group.key}
                      group={group}
                      currentTotalAmount={groupState.totalAmount}
                      units={groupState.units}
                      currentPersonAmounts={groupState.personAmounts}
                      captured={captured}
                      isLast={
                        index === activeDish.allIngredientGroups.length - 1
                      }
                      onCardClick={() => openProductSelection(group)}
                      onTotalAmountChange={(amt) =>
                        handleTotalAmountChange(group.key, group, amt)
                      }
                      onTotalUnitChange={(unit) =>
                        handleTotalUnitChange(group.key, group, unit)
                      }
                      onPersonAmountChange={(pId, amt) =>
                        handlePersonAmountChange(group.key, group, pId, amt)
                      }
                      onSplitValues={() => handleSplitValues(group.key, group)}
                      onDeleteGroup={() => handleDeleteGroup(group.key, group)}
                      onSelectAlternative={(food) =>
                        handleSelectAlternative(group.key, group, food)
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <>
                {/* ── Rezept-Card ──────────────────────────────────────── */}
                <div className="rounded-card border border-line bg-surface-card shadow-card p-4 flex flex-col gap-3">
                  <SectionLabel>Rezept</SectionLabel>
                  {activeDish.steps.map((step, si) => {
                    const stepNumber = (step.stepIndex ?? si) + 1;
                    const visibleGroups = step.ingredientGroups.filter(
                      (group) => !overrides[group.key]?.deleted,
                    );
                    return (
                      <div key={si}>
                        <p className="font-semibold text-sm text-ink mt-2 mb-1">
                          {`Schritt ${stepNumber}`}
                          {step.stepText ? `: ${step.stepText}` : ""}
                        </p>
                        {visibleGroups.map((group) => {
                          const groupState = getGroupOverride(group);
                          const label = `${groupState.totalAmount.toLocaleString("de-DE")} × ${groupState.units.current?.description ?? ""}`;
                          return (
                            <div
                              key={group.key}
                              className="flex items-baseline justify-between py-0.5"
                            >
                              <span className="text-sm text-ink">
                                {group.name}
                              </span>
                              <span className="text-sm text-ink-muted ml-2 shrink-0">
                                {label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* ── Alle Zutaten (flach aufgelistet) ─────────────────── */}
                <Accordion>
                  <AccordionItem
                    value={"all-ingredients"}
                    className="rounded-card border border-line bg-surface-card shadow-card"
                  >
                    <AccordionTrigger className="px-3 py-3 hover:no-underline">
                      <div className="flex justify-between w-full items-center">
                        <SectionLabel noMargin>Alle Zutaten</SectionLabel>
                        <div className="rounded-card border border-line flex justify-center items-center p-2">
                          <Glyph icon="atListReceipt" />
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-3">
                      {activeDish.allIngredientGroups
                        .filter((group) => !overrides[group.key]?.deleted)
                        .map((group) => {
                          const groupState = getGroupOverride(group);
                          const label = `${groupState.totalAmount.toLocaleString("de-DE")} × ${groupState.units.current?.description ?? ""}`;
                          return (
                            <div
                              key={group.key}
                              className="flex items-baseline justify-between py-0.5"
                            >
                              <span className="text-sm text-ink">
                                {group.name}
                              </span>
                              <span className="text-sm text-ink-muted ml-2 shrink-0">
                                {label}
                              </span>
                            </div>
                          );
                        })}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </>
            )}

            {/* ── Nährwerte je Person ───────────────────────────────────── */}
            {persons.map((person) => {
              const nutri = computePersonNutri(
                activeDish.meals,
                person.id,
                purchases,
                foods,
                overrides,
              );
              return (
                <PersonNutriCard
                  key={person.id}
                  name={person.name}
                  nutri={nutri}
                />
              );
            })}

            {/* ── Kochen- oder Direkt-Verzehr-Button ───────────────────── */}
            <div className="flex justify-center pt-2">
              {activeDish.isNoDishGroup ? (
                <button
                  data-cy="cooking-save-intake-button"
                  className="w-full rounded-card bg-teal text-white py-3.5 font-semibold text-sm flex items-center justify-center gap-2 active:opacity-80 transition-opacity"
                  onClick={async () => {
                    props.clearHistory();
                    for (const meal of activeDish.meals) {
                      const mealKey = getMealKeyForMeal(meal.meal);
                      const allDeleted = meal.meal.mealIngredients.every(
                        (mi) => {
                          const override = overrides[getMIKey(mi)];
                          return (
                            (override?.deleted &&
                              (meal.meal.dishId ?? 0) > 0) ||
                            override.personAmounts[meal.meal.personId] === 0
                          );
                        },
                      );
                      if (allDeleted) {
                        props.onDelete(
                          date,
                          mealInd,
                          mealKey,
                          meal.meal.personId,
                        );
                      } else {
                        props.onConsume(
                          date,
                          mealInd,
                          mealKey,
                          overrides,
                          meal.meal.personId,
                        );
                      }
                    }
                  }}
                >
                  ✓ Verzehr speichern
                </button>
              ) : (
                <button
                  data-cy="cooking-start-button"
                  className="w-full rounded-card bg-teal text-white py-3.5 font-semibold text-sm flex items-center justify-center gap-2 active:opacity-80 transition-opacity"
                  onClick={openStepDrawer}
                >
                  Kochen
                </button>
              )}
            </div>
          </div>
        }
      />

      {/* ════════════════════════════════════════════════════════════════════
          Step-by-step drawer
          ════════════════════════════════════════════════════════════════════ */}
      <Plan2PlateDrawer
        noScroll
        onClose={stepDisclosure.onClose}
        open={stepDisclosure.isOpen}
        header={
          <div className="flex items-center justify-between w-full pr-4">
            <div className="flex-1 min-w-0">
              <div className="font-bold leading-tight flex items-center gap-1.5 text-base text-ink">
                <span className="truncate">
                  {activeDish.description} kochen
                </span>
              </div>
              <div className="text-xs text-ink-muted mt-0.5">
                {mealIndName} · {formatGermanDateShort(date)}
              </div>
            </div>
          </div>
        }
        body={
          <div
            className="flex h-full min-h-0 w-full flex-col gap-4 pb-6"
            {...stepSwipe}
          >
            {recipeLoading && (
              <LookupSkeleton dataCy="cooking-step-loading" lines={4} />
            )}

            {!recipeLoading && (
              <>
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                  {/* Segmented progress bar (Gamification) */}
                  <StepProgressBar
                    count={steps.length}
                    currentIndex={cookingStepIndex}
                  />

                  {/* Step navigation label */}
                  {hasStepNav && currentStep && (
                    <p className="text-sm font-semibold text-ink">
                      Schritt {(currentStep.stepIndex ?? cookingStepIndex) + 1}{" "}
                      von {steps.length}
                    </p>
                  )}

                  {/* Step card */}
                  {currentStep && (
                    <>
                      <p className="rounded-card border border-line bg-surface p-3 text-sm text-ink-muted">
                        {currentStep.stepText || "Keine Beschreibung"}
                      </p>

                      {visibleStepGroups.length > 0 && (
                        <div className="rounded-card border border-line bg-surface-card shadow-card">
                          {visibleStepGroups.map((group, index) => {
                            const groupState = getGroupOverride(group);
                            const captured = groupState.purchaseId;

                            return (
                              <StepIngredientCard
                                key={group.key}
                                group={group}
                                currentTotalAmount={groupState.totalAmount}
                                units={groupState.units}
                                currentPersonAmounts={groupState.personAmounts}
                                captured={captured}
                                isLast={index === visibleStepGroups.length - 1}
                                onCardClick={() => openProductSelection(group)}
                                onTotalAmountChange={(amt) =>
                                  handleTotalAmountChange(group.key, group, amt)
                                }
                                onTotalUnitChange={(unit) =>
                                  handleTotalUnitChange(group.key, group, unit)
                                }
                                onPersonAmountChange={(pId, amt) =>
                                  handlePersonAmountChange(
                                    group.key,
                                    group,
                                    pId,
                                    amt,
                                  )
                                }
                                onSplitValues={() =>
                                  handleSplitValues(group.key, group)
                                }
                                onDeleteGroup={() =>
                                  handleDeleteGroup(group.key, group)
                                }
                                onSelectAlternative={(food) =>
                                  handleSelectAlternative(
                                    group.key,
                                    group,
                                    food,
                                  )
                                }
                              />
                            );
                          })}
                        </div>
                      )}

                      {(() => {
                        const inactiveGroups =
                          currentStep.ingredientGroups.filter((group) => {
                            const groupState = getGroupOverride(group);
                            return (
                              groupState.deleted || groupState.totalAmount === 0
                            );
                          });

                        if (inactiveGroups.length === 0) return null;

                        return (
                          <>
                            {inactiveGroups.map((group) => {
                              const groupState = getGroupOverride(group);
                              const foodUnitId = resolveFoodUnitId(
                                groupState.units.unitId,
                                groupState.purchaseId,
                                purchases,
                                foods,
                              );
                              const currentFood =
                                groupState.units.unitId && foodUnitId
                                  ? getFoodMaybe(foodUnitId, foods)
                                  : undefined;
                              const displayName = currentFood
                                ? formatFoodDescription(currentFood)
                                : group.name;
                              return (
                                <IngredientRestoreRow
                                  key={group.key}
                                  name={displayName}
                                  onRestore={() => {
                                    const si = group.recipeIngredient;
                                    const defaultAmount =
                                      si && si.amount > 0 ? si.amount : 1;
                                    const share =
                                      defaultAmount / persons.length;

                                    updateOverrides((draft) => {
                                      const override = draft[group.key];
                                      if (override) {
                                        override.deleted = false;
                                        for (const pId of Object.keys(
                                          override.personAmounts,
                                        )) {
                                          const personId = Number(pId);

                                          const isEatingAnotherAlternative =
                                            currentStep.ingredientGroups.some(
                                              (otherGroup) => {
                                                if (
                                                  otherGroup === group ||
                                                  otherGroup.recipeIngredient !==
                                                    group.recipeIngredient
                                                )
                                                  return false;
                                                const otherState =
                                                  getGroupOverride(otherGroup);
                                                return (
                                                  !otherState.deleted &&
                                                  (otherState.personAmounts[
                                                    personId
                                                  ] || 0) > 0
                                                );
                                              },
                                            );

                                          override.personAmounts[personId] =
                                            isEatingAnotherAlternative
                                              ? 0
                                              : share;
                                        }
                                      }
                                    });
                                  }}
                                />
                              );
                            })}
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between gap-3 pt-2">
                  {!isFirstStep && (
                    <button
                      className="flex items-center gap-2 px-5 py-3.5 rounded-card font-semibold text-sm ml-auto border border-line"
                      onClick={() =>
                        setCookingStepIndex((i) => Math.max(0, i - 1))
                      }
                    >
                      ←
                    </button>
                  )}
                  {!isLastStep ? (
                    <button
                      data-cy="cooking-next-step-button"
                      className="flex flex-1 rounded-card bg-gold text-white py-3.5 font-semibold text-sm items-center justify-center gap-2 active:opacity-80 transition-opacity border border-gold"
                      onClick={() =>
                        setCookingStepIndex((i) =>
                          Math.min(steps.length - 1, i + 1),
                        )
                      }
                    >
                      → Nächster Schritt
                    </button>
                  ) : (
                    <button
                      data-cy="cooking-step-save-button"
                      className="flex w-full rounded-card bg-teal text-white py-3.5 font-semibold text-sm items-center justify-center gap-2 active:opacity-80 transition-opacity"
                      onClick={async () => {
                        props.clearHistory();
                        props.onConsume(
                          date,
                          mealInd,
                          parseMealKey(activeDish.keyString as MealKeyString),
                          overrides,
                        );
                      }}
                    >
                      <span>✓ Verzehr speichern</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        }
      />

      {/* ════════════════════════════════════════════════════════════════════
          Product selection drawer
          ════════════════════════════════════════════════════════════════════ */}
      {selectedGroup && (
        <CookingProductPicker
          open={productDisclosure.isOpen}
          onClose={productDisclosure.onClose}
          group={selectedGroup}
          activeUnitId={getGroupOverride(selectedGroup).units.unitId}
          activeKind={getGroupOverride(selectedGroup).units.kind}
          capturedPurchaseId={getGroupOverride(selectedGroup).purchaseId}
          onSelect={(id) => handleCaptureProduct(id)}
          onOpenCreate={createPurchaseDisclosure.onOpen}
        />
      )}
    </>
  );
}
